import React, { useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import AdminHeader from '../components/Admin/AdminHeader';
import AdminLogin from '../components/Admin/AdminLogin';
import AdminProfile from '../components/Admin/AdminProfile';
import MessageModeration from '../components/Admin/MessageModeration';
import UserModeration from '../components/Admin/UserModeration';
import StreamSettings from '../components/Admin/StreamSettings';
import AdminOverview from '../components/Admin/AdminOverview';
import UserAnalytics from '../components/Admin/UserAnalytics';
import PollsModerator from '../components/Admin/PollsModerator';
import CustomRolesManager from '../components/Admin/CustomRolesManager';
import UpcomingEventsManager from '../components/Admin/UpcomingEventsManager';
import ModeratorNotifications from '../components/Admin/ModeratorNotifications';
import StreamHealthMonitor from '../components/Admin/StreamHealthMonitor';
import ReportsQueueManager from '../components/Admin/ReportsQueueManager';
import SlowModeControl from '../components/Admin/SlowModeControl';
import AuditLogsViewer from '../components/Admin/AuditLogsViewer';
import useAdminModeration from '../components/Admin/useAdminModeration';
import usePlatformSettings from '../components/Admin/usePlatformSettings';
import { ADMIN_TABLE } from '../components/Chat/chatConstants';
import supabase from '../supabase/supabase';

const {
  FiLayout,
  FiMessageSquare,
  FiMonitor,
  FiMenu,
  FiX,
  FiShield,
  FiTrendingUp,
  FiBarChart2,
  FiCalendar,
  FiBell,
  FiSun,
  FiMoon,
  FiActivity,
  FiAlertTriangle,
  FiFileText
} = FiIcons;

function Dashboard({ session, initialProfile, onLogout }) {
  const [profile, setProfile] = useState(initialProfile);
  const [profileOpen, setProfileOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [presenceCount, setPresenceCount] = useState(1);
  const { messages, users, restrictions, error: modError, moderate, restore, removeMessage, clearAllMessages } =
    useAdminModeration(session);
  const { settings, error: settingsError, updateSettings } = usePlatformSettings();

  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('gallo_theme') || 'dark';
  });

  useEffect(() => {
    const root = window.document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
      root.classList.remove('light');
    } else {
      root.classList.add('light');
      root.classList.remove('dark');
    }
    localStorage.setItem('gallo_theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  useEffect(() => {
    const channel = supabase.channel('gallo_chat_presence');
    const syncPresence = () => {
      const state = channel.presenceState();
      const count = Object.keys(state).length;
      setPresenceCount(count > 0 ? count : 1);
    };
    channel.on('presence', { event: 'sync' }, syncPresence).subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const menuItems = [
    { id: 'overview', label: 'Resumen Global', icon: FiLayout },
    { id: 'health', label: 'Monitor Salud Señal', icon: FiActivity },
    { id: 'reports', label: 'Cola Reportes', icon: FiAlertTriangle },
    { id: 'analytics', label: 'Métricas Usuarios', icon: FiTrendingUp },
    { id: 'polls', label: 'Encuestas en Vivo', icon: FiBarChart2 },
    { id: 'chat', label: 'Moderación Chat', icon: FiMessageSquare },
    { id: 'audit', label: 'Auditoría Acciones', icon: FiFileText },
    { id: 'alerts', label: 'Alertas Palabras', icon: FiBell },
    { id: 'events', label: 'Próximos Eventos', icon: FiCalendar },
    { id: 'roles', label: 'Roles y Equipo', icon: FiShield },
    { id: 'stream', label: 'Señal de Video', icon: FiMonitor }
  ];

  return (
    <div className="h-screen w-screen bg-gray-50 dark:bg-black text-gray-900 dark:text-white flex flex-col md:flex-row font-sans transition-colors duration-200 overflow-hidden">
      {/* Sidebar Fijo a la izquierda */}
      <aside className="hidden md:flex flex-col w-64 bg-white dark:bg-[#0a0a0a] border-r border-gray-200 dark:border-white/10 shrink-0 h-full">
        <div className="p-6 border-b border-gray-200 dark:border-white/10 flex items-center gap-3 shrink-0">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-red-600 text-white shadow-lg shadow-red-600/20">
            <SafeIcon icon={FiShield} size={18} />
          </span>
          <div>
            <h1 className="font-bold text-sm tracking-tight text-gray-900 dark:text-white uppercase">El Gallo Live</h1>
            <p className="text-[10px] text-gray-500">Panel de Control Pro</p>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-xs font-medium transition-all ${
                activeTab === item.id
                  ? 'bg-red-600 text-white shadow-lg shadow-red-600/10 font-bold'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <SafeIcon icon={item.icon} size={14} />
              {item.label}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-black/40 shrink-0">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-8 w-8 rounded-full bg-red-600/10 flex items-center justify-center text-red-500 text-xs font-bold">
              {profile.username?.slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-gray-900 dark:text-white truncate">@{profile.username}</p>
              <p className="text-[10px] text-gray-500 truncate">{profile.email}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setProfileOpen(true)}
              className="flex-1 py-1.5 text-center text-[11px] font-medium rounded-lg border border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
            >
              Perfil
            </button>
            <button
              type="button"
              onClick={onLogout}
              className="flex-1 py-1.5 text-center text-[11px] font-medium rounded-lg bg-red-600/10 text-red-400 hover:bg-red-600/20 transition-colors"
            >
              Salir
            </button>
          </div>
        </div>
      </aside>

      {/* Header móvil */}
      <div className="md:hidden flex items-center justify-between px-4 py-3 bg-white dark:bg-[#0a0a0a] border-b border-gray-200 dark:border-white/10 shrink-0">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-600 text-white">
            <SafeIcon icon={FiShield} size={16} />
          </span>
          <span className="font-bold text-sm text-gray-900 dark:text-white uppercase tracking-wider">El Gallo Live</span>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={toggleTheme}
            title={theme === 'dark' ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
            className="p-1.5 rounded-lg border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5"
          >
            <SafeIcon icon={theme === 'dark' ? FiSun : FiMoon} size={16} />
          </button>
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="p-1.5 rounded-lg border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5"
          >
            <SafeIcon icon={FiMenu} size={20} />
          </button>
        </div>
      </div>

      {/* Menú deslizante móvil */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 flex md:hidden">
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
          <div className="relative flex flex-col w-72 max-w-[80vw] bg-white dark:bg-[#0a0a0a] h-full border-r border-gray-200 dark:border-white/10 p-5">
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200 dark:border-white/5 shrink-0">
              <span className="font-bold text-sm text-gray-900 dark:text-white uppercase tracking-wider">Menú Principal</span>
              <button onClick={() => setSidebarOpen(false)} className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
                <SafeIcon icon={FiX} size={20} />
              </button>
            </div>
            <nav className="flex-1 space-y-1 overflow-y-auto">
              {menuItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveTab(item.id);
                    setSidebarOpen(false);
                  }}
                  className={`flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-all ${
                    activeTab === item.id ? 'bg-red-600 text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  <SafeIcon icon={item.icon} size={16} />
                  {item.label}
                </button>
              ))}
            </nav>
            <div className="pt-4 border-t border-gray-200 dark:border-white/5 mt-auto shrink-0">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Conectado como <strong>@{profile.username}</strong></p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setProfileOpen(true);
                    setSidebarOpen(false);
                  }}
                  className="py-2 text-center text-xs font-semibold rounded-xl border border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-300">
                  Perfil
                </button>
                <button type="button" onClick={onLogout} className="py-2 text-center text-xs font-semibold rounded-xl bg-red-600/20 text-red-400">
                  Salir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contenido principal con scroll vertical completo */}
      <div className="flex-1 flex flex-col h-full min-w-0 overflow-hidden bg-gray-50 dark:bg-black transition-colors duration-200">
        <header className="hidden md:flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-white/10 bg-white dark:bg-[#0a0a0a] shrink-0">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
              {activeTab === 'overview' && 'Resumen de la Plataforma'}
              {activeTab === 'health' && 'Monitor de Salud de Señal'}
              {activeTab === 'reports' && 'Cola de Reportes de Espectadores'}
              {activeTab === 'analytics' && 'Métricas & Estadísticas de Usuarios'}
              {activeTab === 'polls' && 'Encuestas Interactivas & Exportación'}
              {activeTab === 'chat' && 'Moderación de Chat en Vivo & Modo Lento'}
              {activeTab === 'audit' && 'Registro de Auditoría de Moderación'}
              {activeTab === 'alerts' && 'Alertas de Chat'}
              {activeTab === 'events' && 'Gestión de Próximos Eventos'}
              {activeTab === 'roles' && 'Asignación de Roles y Equipo'}
              {activeTab === 'stream' && 'Configuración de Transmisión'}
            </h2>
            <p className="text-xs text-gray-500">Gestión integrada en tiempo real de tu señal y espectadores</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={toggleTheme}
              title={theme === 'dark' ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
              className="flex items-center justify-center h-9 w-9 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 transition-all shadow-sm"
            >
              <SafeIcon icon={theme === 'dark' ? FiSun : FiMoon} size={16} />
            </button>
            <a
              href="#/"
              className="flex items-center gap-2 rounded-xl bg-white/5 hover:bg-gray-100 dark:hover:bg-white/10 border border-gray-200 dark:border-white/10 px-4 py-2 text-xs font-semibold text-gray-600 dark:text-gray-300 transition-colors"
            >
              Ver Plataforma En Vivo
            </a>
          </div>
        </header>

        {/* Área de desplazamiento fluido */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6 w-full max-w-7xl mx-auto space-y-6 pb-28">
          {(modError || settingsError) && (
            <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-xs sm:text-sm text-red-400">
              {modError || settingsError}
            </div>
          )}

          {activeTab === 'overview' && (
            <div className="space-y-6">
              <AdminOverview
                settings={settings}
                onUpdateSettings={updateSettings}
                messages={messages}
                restrictions={restrictions}
                onlineCount={presenceCount}
              />
              <StreamHealthMonitor settings={settings} />
            </div>
          )}

          {activeTab === 'health' && (
            <div className="space-y-6">
              <StreamHealthMonitor settings={settings} />
            </div>
          )}

          {activeTab === 'reports' && (
            <div className="max-w-4xl space-y-6">
              <ReportsQueueManager adminProfile={profile} />
            </div>
          )}

          {activeTab === 'analytics' && (
            <UserAnalytics messages={messages} users={users} onlineCount={presenceCount} />
          )}

          {activeTab === 'polls' && (
            <div className="max-w-3xl">
              <PollsModerator />
            </div>
          )}

          {activeTab === 'chat' && (
            <div className="space-y-6">
              <SlowModeControl
                settings={settings}
                onUpdateSettings={updateSettings}
                adminProfile={profile}
              />
              <div className="grid gap-6 lg:grid-cols-2">
                <UserModeration
                  users={users}
                  restrictions={restrictions}
                  onModerate={moderate}
                  onRestore={restore}
                />
                <MessageModeration
                  messages={messages}
                  onRemove={removeMessage}
                  onClearAll={clearAllMessages}
                />
              </div>
            </div>
          )}

          {activeTab === 'audit' && (
            <div className="max-w-4xl">
              <AuditLogsViewer />
            </div>
          )}

          {activeTab === 'alerts' && (
            <div className="max-w-3xl">
              <ModeratorNotifications messages={messages} onRemoveMessage={removeMessage} />
            </div>
          )}

          {activeTab === 'events' && (
            <div className="max-w-3xl">
              <UpcomingEventsManager />
            </div>
          )}

          {activeTab === 'roles' && (
            <div className="max-w-3xl">
              <CustomRolesManager />
            </div>
          )}

          {activeTab === 'stream' && (
            <div className="max-w-3xl space-y-6">
              <StreamSettings settings={settings} onUpdate={updateSettings} />
            </div>
          )}
        </main>
      </div>

      {profileOpen && (
        <AdminProfile
          session={session}
          profile={profile}
          onClose={() => setProfileOpen(false)}
          onUpdated={(changes) => setProfile((current) => ({ ...current, ...changes }))}
        />
      )}
    </div>
  );
}

export default function AdminPanel() {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [checking, setChecking] = useState(true);

  const verifyAdministrator = async (nextSession) => {
    if (!nextSession) {
      setSession(null);
      setProfile(null);
      setChecking(false);
      return;
    }
    const { data } = await supabase
      .from(ADMIN_TABLE)
      .select('id,user_id,email,username')
      .eq('user_id', nextSession.user.id)
      .maybeSingle();

    setSession(data ? nextSession : null);
    setProfile(data || null);
    setChecking(false);
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      verifyAdministrator(data.session);
    });
    const { data: listener } = supabase.auth.onAuthStateChange((event, nextSession) => {
      verifyAdministrator(nextSession);
    });
    return () => listener.subscription.unsubscribe();
  }, []);

  const logout = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setProfile(null);
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-black text-sm text-gray-500">
        Verificando acceso...
      </div>
    );
  }

  if (!session || !profile) {
    return <AdminLogin onAuthenticated={verifyAdministrator} />;
  }

  return <Dashboard session={session} initialProfile={profile} onLogout={logout} />;
}